import React from 'react';
import Helmet from '../components/Helmet/Helmet';
import CommonSection from '../components/Ui/CommonSection';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../styles/about.css';
import bedroomImg from '../assets/images/bedroom.jpg';

const materials = [
  {
    icon: 'ri-leaf-line',
    title: 'Kiln-Dried Hardwood',
    text: 'Sustainably harvested European ash and FSC-certified solid oak, kiln-dried to eliminate tension and guarantee lifelong dimensional stability.',
    origin: 'FSC® Certified Forestry &bull; France & Austria',
  },
  {
    icon: 'ri-t-shirt-air-line',
    title: 'Heritage Bouclé & Velvet',
    text: 'Textured natural wool bouclés and Belgian cotton velvets dyed with organic botanical pigments, yielding incomparable softness and enduring resilience.',
    origin: 'Textile Mills &bull; Flanders, Belgium',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Full-Grain Tuscan Leathers',
    text: 'Supple hides treated with traditional vegetable tanning, allowing the patina to develop a rich, personal story over decades of daily intimacy.',
    origin: 'Artisanal Tanneries &bull; Veneto & Tuscany, Italy',
  },
  {
    icon: 'ri-time-line',
    title: 'Horological Precision',
    text: 'Hand-polished surgical 316L stainless steel, sapphire crystal dials, and Swiss-grade internal calibers crafted for perpetual accuracy.',
    origin: 'Horology Ateliers &bull; Jura Mountains, Switzerland',
  },
];

const milestones = [
  {
    year: '2016',
    title: 'The Paris Workshop',
    desc: 'Founded as a bespoke studio in the 3rd arrondissement of Paris, crafting custom chairs and sofas for private residences.',
  },
  {
    year: '2019',
    title: 'Architectural Expansion',
    desc: 'Introduced our signature minimalist metal and solid timber collection, awarded the European Design Craftsmanship Honor.',
  },
  {
    year: '2022',
    title: 'Horology & Living',
    desc: 'Expanded into horological living accessories and precision study timepieces, marrying architecture with precision mechanics.',
  },
  {
    year: '2026',
    title: 'Global Flagship Salons',
    desc: 'Direct atelier presence in Paris, Milan, Cairo, and Dubai, delivering white-glove bespoke interiors across the globe.',
  },
];

const About = () => {
  return (
    <Helmet title="The Atelier & Heritage | L'HABILLEUR">
      <CommonSection title="The Atelier & Heritage" />

      {/* Hero Story Section */}
      <section className="about__hero-section">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg="6">
              <span className="about__hero-subtitle">Our Philosophy</span>
              <h2 className="about__hero-title">
                Timeless Form, Honest Material, Unhurried Craft.
              </h2>
              <p className="about__hero-lead mb-4">
                At <strong>L’HABILLEUR</strong>, we view interior spaces not merely as rooms, but as sanctuaries of stillness, beauty, and intimate memory.
              </p>
              <p className="text-muted mb-4">
                Every silhouette is born from deep reverence for architectural discipline and tactile warmth. We consciously reject seasonal trends in favor of enduring sculptural honesty. By partnering with master joiners, tanners, and clockmakers who have perfected their arts across generations, we produce objects meant to be treasured and passed down.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <Link to="/shop" className="buy__btn">
                  Explore The Collection
                </Link>
                <Link to="/lookbook" className="btn btn-outline-dark px-4 py-2" style={{ borderRadius: 'var(--radius-btn)', fontWeight: '600' }}>
                  View Editorial Spaces &rarr;
                </Link>
              </div>
            </Col>

            <Col lg="6">
              <div className="about__hero-img-box">
                <img src={bedroomImg} alt="L'Habilleur Atelier interior space" />
                <div className="about__hero-badge">
                  <span>Certified Heritage</span>
                  <strong>100% Handcrafted Artisanal Standard</strong>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Craftsmanship & Materials Section */}
      <section className="about__materials-section text-center">
        <Container>
          <span className="about__section-badge">Ethical Integrity</span>
          <h2 className="about__section-title">The Materials of Sanctuary</h2>
          <p className="about__section-desc">
            We source our noble woods, hides, and metals strictly from certified ecological suppliers who share our uncompromising standards of purity.
          </p>

          <Row className="g-4 text-start">
            {materials.map((mat, idx) => (
              <Col key={idx} lg="3" md="6">
                <div className="about__material-card">
                  <div className="about__material-icon">
                    <i className={mat.icon}></i>
                  </div>
                  <h4 className="about__material-title">{mat.title}</h4>
                  <p className="about__material-text">{mat.text}</p>
                  <div className="about__material-origin" dangerouslySetInnerHTML={{ __html: mat.origin }}></div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Quote / Atelier Manifesto */}
      <section className="about__quote-section">
        <Container>
          <div className="about__quote-wrapper">
            <i className="ri-double-quotes-l about__quote-icon"></i>
            <p className="about__quote-text">
              "We do not design furniture to fill an empty room. We design quiet centerpieces that quiet the mind and elevate everyday human presence."
            </p>
            <div className="about__quote-author">Jean-Luc Moreau</div>
            <div className="about__quote-role">Head of Atelier Architecture &bull; Paris</div>
          </div>
        </Container>
      </section>

      {/* Historical Milestones */}
      <section className="about__timeline-section">
        <Container>
          <div className="text-center">
            <span className="about__section-badge">Atelier History</span>
            <h2 className="about__section-title">A Decade of Architectural Purity</h2>
            <p className="about__section-desc">
              From an artisanal Parisian workshop to an international luxury presence serving private villas, penthouses, and boutique hotels.
            </p>
          </div>

          <div className="about__timeline-grid">
            {milestones.map((item, idx) => (
              <div key={idx} className="about__timeline-card">
                <div className="about__timeline-year">{item.year}</div>
                <h5 className="about__timeline-title">{item.title}</h5>
                <p className="about__timeline-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Atelier Tour CTA */}
      <section className="py-5 bg-white border-top">
        <Container className="text-center py-4">
          <h3 className="fw-bold mb-3" style={{ color: 'var(--primary-color)' }}>
            Experience the Atelier in Person
          </h3>
          <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '600px' }}>
            Book a private one-on-one session at our Cairo, Paris, Milan, or Dubai showrooms with a senior interior curator.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Link to="/contact" className="buy__btn">
              <i className="ri-map-pin-line me-1"></i> View Showrooms & Concierge
            </Link>
            <Link to="/services" className="btn btn-outline-dark px-4 py-2" style={{ borderRadius: 'var(--radius-btn)', fontWeight: '600' }}>
              Bespoke Interior Services
            </Link>
          </div>
        </Container>
      </section>
    </Helmet>
  );
};

export default About;
